import { z } from "zod";
/**
 * A member's progress through one course. Counts, not percentages, are the
 * source of truth: `percent` is derived from them so every surface rounds the
 * same way.
 */
export const CourseProgress = z.object({
    completedModules: z.number().int().nonnegative(),
    totalModules: z.number().int().nonnegative(),
    percent: z.number().int().min(0).max(100),
    startedAt: z.string().nullable(),
    lastWatchedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    /** The module to resume or start next. Null once every module is done. */
    nextModuleId: z.string().nullable(),
});
export const CourseExpert = z.object({
    name: z.string(),
    title: z.string().nullable(),
    photoUrl: z.string().nullable(),
});
/** Health behaviour keys a course can belong to; null when the CMS names none HealthOS maps. */
export const CourseBehaviour = z.enum(["sleep", "food", "movement", "stress", "connection"]);
export const CourseSummary = z.object({
    id: z.string(),
    title: z.string(),
    subtitle: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    behaviour: CourseBehaviour.nullable(),
    lessonCount: z.number().int().nonnegative(),
    moduleCount: z.number().int().nonnegative(),
    /** Sum of the durations the CMS knows. Null when it knows none of them. */
    durationSeconds: z.number().int().nonnegative().nullable(),
    experts: z.array(CourseExpert),
    progress: CourseProgress,
});
export const CourseModuleProgress = z.object({
    positionSeconds: z.number().int().nonnegative(),
    durationSeconds: z.number().int().positive().nullable(),
    completedAt: z.string().nullable(),
});
export const CourseModule = z.object({
    id: z.string(),
    title: z.string(),
    byline: z.string().nullable(),
    durationSeconds: z.number().int().positive().nullable(),
    thumbnailUrl: z.string().nullable(),
    videoUrl: z.string(),
    progress: CourseModuleProgress.nullable(),
});
export const CourseLesson = z.object({
    id: z.string(),
    title: z.string(),
    modules: z.array(CourseModule),
});
export const CourseDetail = CourseSummary.extend({
    about: z.string().nullable(),
    blurbs: z.array(z.string()),
    tags: z.array(z.string()),
    lessons: z.array(CourseLesson),
});
export const ListCoursesResponse = z.object({ courses: z.array(CourseSummary) });
export const GetCourseParams = z.object({ courseId: z.string().trim().min(1).max(64) });
export const GetCourseResponse = z.object({ course: CourseDetail });
export const SaveCourseModuleProgressParams = z.object({
    courseId: z.string().trim().min(1).max(64),
    moduleId: z.string().trim().min(1).max(64),
});
export const SaveCourseModuleProgressRequest = z.object({
    /** Where playback is now. Progress never moves backwards on the server. */
    positionSeconds: z.number().nonnegative().max(86_400),
    /** The length the player reports, which half the CMS modules lack. */
    durationSeconds: z.number().positive().max(86_400).nullish(),
    /** The player reached the end. */
    ended: z.boolean().optional(),
});
export const SaveCourseModuleProgressResponse = z.object({
    module: CourseModuleProgress,
    course: CourseProgress,
    /** True only on the save that finished the module, so clients celebrate once. */
    moduleJustCompleted: z.boolean(),
    /** True only on the save that finished the whole course. */
    courseJustCompleted: z.boolean(),
});
//# sourceMappingURL=courses.js.map